import type { Block } from "@/types/proposals";

interface CoverBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  onSelect: () => void;
}

export function CoverBlock({ block }: CoverBlockProps) {
  const { coverStyle = 'diagonal', ...content } = block.content;
  
  switch (coverStyle) {
    case 'framed':
      return <FramedCover content={content} />;
    case 'split':
      return <SplitCover content={content} />;
    case 'wave':
      return <WaveCover content={content} />;
    case 'bold':
      return <BoldCover content={content} />;
    case 'corporate':
      return <CorporateCover content={content} />;
    case 'diagonal':
    default:
      return <DiagonalCover content={content} />;
  }
}

// Diagonal Cover (Modern) - Original design with diagonal stripe
function DiagonalCover({ content }: { content: any }) {
  const { 
    companyLogo,
    companyName = "Your Company Name",
    companyTagline = "Professional Roofing Services",
    serviceType = "Roof Replacement",
    date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto,
    salesRepName = "Sales Representative",
    companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567",
    customerName = "Homeowner Name",
    customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345",
    brandColor = "#2BC5B4",
  } = content;

  return (
    <div className="relative w-full h-full bg-white flex flex-col">
      {/* Header Section */}
      <div className="flex items-start justify-between p-8 pb-6 relative z-10">
        <div className="flex items-center gap-4 max-w-[60%]">
          {companyLogo && (
            <img src={companyLogo} alt="Company Logo" className="w-16 h-16 object-contain" />
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">{companyName}</h2>
            <p className="text-sm text-gray-600 mt-0.5">{companyTagline}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-gray-900">{serviceType}</div>
          <div className="text-sm text-gray-600 mt-1">{date}</div>
        </div>
      </div>

      {/* Diagonal Brand Stripe */}
      <div 
        className="absolute top-0 left-0 w-full h-[45%] z-0"
        style={{
          backgroundColor: brandColor,
          opacity: 0.15,
          clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0 100%)',
        }}
      />

      {/* Property Photo */}
      <div className="flex-1 flex items-center justify-center px-12 py-8 relative z-10">
        {propertyPhoto ? (
          <div className="w-full max-w-2xl">
            <img src={propertyPhoto} alt="Property" className="w-full h-auto rounded-lg shadow-2xl object-cover" style={{ maxHeight: '400px' }} />
          </div>
        ) : (
          <div className="w-full max-w-2xl h-64 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
            <p className="text-gray-400 text-sm">Property Photo</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="grid grid-cols-2 gap-8 px-8 pb-8 pt-4 border-t border-gray-200 relative z-10">
        <div className="space-y-1.5">
          <h3 className="font-bold text-gray-900 text-base mb-2">{companyName}</h3>
          <p className="text-sm text-gray-700"><span className="font-medium">Sales Rep:</span> {salesRepName}</p>
          <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> {companyEmail}</p>
          <p className="text-sm text-gray-700"><span className="font-medium">Phone:</span> {companyPhone}</p>
        </div>
        <div className="space-y-1.5">
          <h3 className="font-bold text-gray-900 text-base mb-2">Prepared For</h3>
          <p className="text-sm text-gray-700 font-medium">{customerName}</p>
          <p className="text-sm text-gray-700">{customerAddress}</p>
          <p className="text-sm text-gray-700">{customerCityStateZip}</p>
        </div>
      </div>
    </div>
  );
}

// Framed Cover (Classic) - Traditional bordered design
function FramedCover({ content }: { content: any }) {
  const { 
    companyLogo, companyName = "Your Company Name", companyTagline = "Professional Services",
    serviceType = "Service Proposal", date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto, salesRepName = "Sales Representative", companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567", customerName = "Client Name", customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345", brandColor = "#1e3a8a",
  } = content;

  return (
    <div className="relative w-full h-full bg-white flex flex-col p-6">
      {/* Ornamental Border */}
      <div className="absolute inset-6 border-4 border-double pointer-events-none" style={{ borderColor: brandColor }} />
      <div className="absolute inset-8 border pointer-events-none" style={{ borderColor: brandColor, opacity: 0.3 }} />
      
      <div className="relative z-10 flex flex-col h-full p-8">
        {/* Centered Header */}
        <div className="text-center space-y-4 mb-8">
          {companyLogo && (
            <img src={companyLogo} alt="Company Logo" className="w-20 h-20 object-contain mx-auto" />
          )}
          <div>
            <h1 className="text-3xl font-serif font-bold" style={{ color: brandColor }}>{companyName}</h1>
            <div className="flex items-center justify-center gap-3 my-3">
              <div className="h-px w-16 bg-gray-400" />
              <p className="text-sm text-gray-600 italic">{companyTagline}</p>
              <div className="h-px w-16 bg-gray-400" />
            </div>
          </div>
          <div className="text-lg font-semibold text-gray-800">{serviceType}</div>
          <div className="text-sm text-gray-600">{date}</div>
        </div>

        {/* Property Photo with Frame */}
        <div className="flex-1 flex items-center justify-center mb-8">
          {propertyPhoto ? (
            <div className="relative p-3 bg-white" style={{ boxShadow: `0 0 0 2px ${brandColor}20, 0 4px 12px rgba(0,0,0,0.15)` }}>
              <img src={propertyPhoto} alt="Property" className="w-full max-w-lg h-auto object-cover" style={{ maxHeight: '350px' }} />
            </div>
          ) : (
            <div className="w-full max-w-lg h-64 bg-gray-50 border-2 border-dashed flex items-center justify-center" style={{ borderColor: brandColor }}>
              <p className="text-gray-400 text-sm">Property Photo</p>
            </div>
          )}
        </div>

        {/* Formal Footer */}
        <div className="border-t-2 pt-6 grid grid-cols-2 gap-8" style={{ borderColor: brandColor }}>
          <div className="space-y-2">
            <h3 className="font-serif font-bold text-base mb-3" style={{ color: brandColor }}>Company Information</h3>
            <p className="text-sm"><span className="font-semibold">Representative:</span> {salesRepName}</p>
            <p className="text-sm"><span className="font-semibold">Email:</span> {companyEmail}</p>
            <p className="text-sm"><span className="font-semibold">Phone:</span> {companyPhone}</p>
          </div>
          <div className="space-y-2">
            <h3 className="font-serif font-bold text-base mb-3" style={{ color: brandColor }}>Prepared For</h3>
            <p className="text-sm font-semibold">{customerName}</p>
            <p className="text-sm">{customerAddress}</p>
            <p className="text-sm">{customerCityStateZip}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Split Cover (Siding) - Bold vertical split
function SplitCover({ content }: { content: any }) {
  const { 
    companyLogo, companyName = "Your Company Name", companyTagline = "Modern Services",
    serviceType = "Service Proposal", date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto, salesRepName = "Sales Representative", companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567", customerName = "Client Name", customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345", brandColor = "#14b8a6",
  } = content;

  return (
    <div className="relative w-full h-full bg-white flex">
      {/* Left Sidebar - Color Bar */}
      <div className="w-[25%] flex flex-col items-center justify-between py-12 px-6 text-white" style={{ backgroundColor: brandColor }}>
        <div className="flex flex-col items-center text-center space-y-6">
          {companyLogo && (
            <img src={companyLogo} alt="Company Logo" className="w-16 h-16 object-contain brightness-0 invert" />
          )}
          <div>
            <h2 className="text-xl font-bold leading-tight mb-2">{companyName}</h2>
            <p className="text-sm opacity-90">{companyTagline}</p>
          </div>
        </div>
        <div className="text-center space-y-3 text-sm">
          <p className="font-semibold">{salesRepName}</p>
          <p className="opacity-90">{companyPhone}</p>
          <p className="opacity-90 text-xs">{companyEmail}</p>
        </div>
      </div>

      {/* Right Main Content */}
      <div className="flex-1 flex flex-col p-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: brandColor }}>{serviceType}</h1>
          <p className="text-gray-600">{date}</p>
        </div>

        {/* Property Photo */}
        <div className="flex-1 mb-8">
          {propertyPhoto ? (
            <img src={propertyPhoto} alt="Property" className="w-full h-full object-cover rounded-lg" style={{ maxHeight: '450px' }} />
          ) : (
            <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center" style={{ maxHeight: '450px' }}>
              <p className="text-gray-400 text-sm">Property Photo</p>
            </div>
          )}
        </div>

        {/* Customer Info */}
        <div className="border-t-2 pt-6" style={{ borderColor: brandColor }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: brandColor }}>Prepared For</h3>
          <p className="text-base font-semibold text-gray-900">{customerName}</p>
          <p className="text-sm text-gray-700">{customerAddress}</p>
          <p className="text-sm text-gray-700">{customerCityStateZip}</p>
        </div>
      </div>
    </div>
  );
}

// Wave Cover (Gutter) - Curved flowing header
function WaveCover({ content }: { content: any }) {
  const { 
    companyLogo, companyName = "Your Company Name", companyTagline = "Flow Solutions",
    serviceType = "Service Proposal", date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto, salesRepName = "Sales Representative", companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567", customerName = "Client Name", customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345", brandColor = "#0ea5e9",
  } = content;

  return (
    <div className="relative w-full h-full bg-white flex flex-col overflow-hidden">
      {/* Wave Header */}
      <div className="relative h-48" style={{ backgroundColor: brandColor }}>
        <svg className="absolute bottom-0 w-full" viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ height: '80px' }}>
          <path d="M0,0 C300,80 600,80 900,40 C1050,20 1150,10 1200,0 L1200,120 L0,120 Z" fill="white" />
        </svg>
        
        <div className="relative z-10 flex items-start justify-between px-8 pt-8 text-white">
          <div className="flex items-center gap-4">
            {companyLogo && (
              <img src={companyLogo} alt="Company Logo" className="w-16 h-16 object-contain brightness-0 invert" />
            )}
            <div>
              <h2 className="text-2xl font-bold">{companyName}</h2>
              <p className="text-sm opacity-90">{companyTagline}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold">{serviceType}</div>
            <div className="text-sm opacity-90">{date}</div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col px-8 py-8">
        {/* Property Photo */}
        <div className="flex-1 flex items-center justify-center mb-8">
          {propertyPhoto ? (
            <div className="w-full max-w-2xl">
              <img src={propertyPhoto} alt="Property" className="w-full h-auto rounded-2xl shadow-lg object-cover" style={{ maxHeight: '400px' }} />
            </div>
          ) : (
            <div className="w-full max-w-2xl h-64 bg-gray-100 rounded-2xl flex items-center justify-center">
              <p className="text-gray-400 text-sm">Property Photo</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="grid grid-cols-2 gap-8 pt-6 border-t" style={{ borderColor: `${brandColor}40` }}>
          <div className="space-y-2">
            <h3 className="font-bold text-base mb-2" style={{ color: brandColor }}>Contact Information</h3>
            <p className="text-sm text-gray-700"><span className="font-medium">Rep:</span> {salesRepName}</p>
            <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> {companyEmail}</p>
            <p className="text-sm text-gray-700"><span className="font-medium">Phone:</span> {companyPhone}</p>
          </div>
          <div className="space-y-2">
            <h3 className="font-bold text-base mb-2" style={{ color: brandColor }}>Prepared For</h3>
            <p className="text-sm text-gray-900 font-medium">{customerName}</p>
            <p className="text-sm text-gray-700">{customerAddress}</p>
            <p className="text-sm text-gray-700">{customerCityStateZip}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Bold Cover (Repair) - Urgent action-oriented
function BoldCover({ content }: { content: any }) {
  const { 
    companyLogo, companyName = "Your Company Name", companyTagline = "Swift Service",
    serviceType = "REPAIR ESTIMATE", date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto, salesRepName = "Sales Representative", companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567", customerName = "Client Name", customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345", brandColor = "#f59e0b",
  } = content;

  return (
    <div className="relative w-full h-full bg-white flex flex-col">
      {/* Angled Bold Header */}
      <div 
        className="relative h-56 text-white flex items-center px-12"
        style={{
          backgroundColor: brandColor,
          clipPath: 'polygon(0 0, 100% 0, 100% 75%, 0 100%)',
        }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-6">
            {companyLogo && (
              <img src={companyLogo} alt="Company Logo" className="w-20 h-20 object-contain brightness-0 invert" />
            )}
            <div>
              <h2 className="text-2xl font-bold">{companyName}</h2>
              <p className="text-base opacity-90">{companyTagline}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Large Service Type */}
      <div className="px-12 -mt-8 relative z-10">
        <h1 className="text-5xl font-black leading-tight" style={{ color: brandColor }}>
          {serviceType}
        </h1>
        <p className="text-gray-600 text-lg mt-2">{date}</p>
      </div>

      {/* Property Photo - Full Width */}
      <div className="flex-1 px-12 py-8">
        {propertyPhoto ? (
          <img src={propertyPhoto} alt="Property" className="w-full h-full object-cover rounded-lg" style={{ maxHeight: '400px' }} />
        ) : (
          <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center" style={{ maxHeight: '400px' }}>
            <p className="text-gray-400 text-sm">Property Photo</p>
          </div>
        )}
      </div>

      {/* Compact Info Bar */}
      <div 
        className="px-12 py-6 text-white flex items-center justify-between"
        style={{ backgroundColor: brandColor }}
      >
        <div>
          <p className="font-bold text-lg">{customerName}</p>
          <p className="text-sm opacity-90">{customerAddress}, {customerCityStateZip}</p>
        </div>
        <div className="text-right">
          <p className="font-semibold">{salesRepName}</p>
          <p className="text-sm opacity-90">{companyPhone}</p>
        </div>
      </div>
    </div>
  );
}

// Corporate Cover (Insurance) - Professional sidebar
function CorporateCover({ content }: { content: any }) {
  const { 
    companyLogo, companyName = "Your Company Name", companyTagline = "Professional Services",
    serviceType = "Insurance Claim Proposal", date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    propertyPhoto, salesRepName = "Sales Representative", companyEmail = "info@company.com",
    companyPhone = "(555) 123-4567", customerName = "Client Name", customerAddress = "123 Main Street",
    customerCityStateZip = "City, ST 12345", brandColor = "#8b5cf6",
  } = content;

  return (
    <div className="relative w-full h-full bg-white flex">
      {/* Left Sidebar - Professional */}
      <div className="w-[30%] flex flex-col py-8 px-6 border-r-4" style={{ borderColor: brandColor, backgroundColor: `${brandColor}08` }}>
        <div className="space-y-6">
          {companyLogo && (
            <img src={companyLogo} alt="Company Logo" className="w-20 h-20 object-contain" />
          )}
          <div>
            <h2 className="text-xl font-bold mb-1" style={{ color: brandColor }}>{companyName}</h2>
            <p className="text-sm text-gray-600">{companyTagline}</p>
          </div>
        </div>

        <div className="mt-auto space-y-6">
          <div className="border-t pt-6" style={{ borderColor: `${brandColor}40` }}>
            <h3 className="text-sm font-bold mb-3 text-gray-900">Company Information</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p><span className="font-medium">Representative:</span><br />{salesRepName}</p>
              <p><span className="font-medium">Email:</span><br />{companyEmail}</p>
              <p><span className="font-medium">Phone:</span><br />{companyPhone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Main Content - Document Style */}
      <div className="flex-1 flex flex-col p-10 bg-white">
        {/* Document Header */}
        <div className="border-b-2 pb-4 mb-8" style={{ borderColor: brandColor }}>
          <h1 className="text-3xl font-bold mb-2" style={{ color: brandColor }}>{serviceType}</h1>
          <p className="text-gray-600">Date: {date}</p>
        </div>

        {/* Property Photo */}
        <div className="flex-1 mb-8">
          {propertyPhoto ? (
            <img src={propertyPhoto} alt="Property" className="w-full h-full object-cover" style={{ maxHeight: '400px' }} />
          ) : (
            <div className="w-full h-full bg-gray-50 border-2 border-dashed flex items-center justify-center" style={{ borderColor: `${brandColor}40`, maxHeight: '400px' }}>
              <p className="text-gray-400 text-sm">Property Photo</p>
            </div>
          )}
        </div>

        {/* Client Information Box */}
        <div className="border-2 p-6 rounded-lg" style={{ borderColor: brandColor }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: brandColor }}>Policy Holder Information</h3>
          <div className="space-y-1">
            <p className="text-base font-semibold text-gray-900">{customerName}</p>
            <p className="text-sm text-gray-700">{customerAddress}</p>
            <p className="text-sm text-gray-700">{customerCityStateZip}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
